import { MgSupplierItemSearchResult } from '@retail/products/types';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { CustomColumnMenu, PaginatedDataGrid, StackedTextCell } from '@retail/components';
import { Dispatch, useCallback, useMemo } from 'react';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { Pagination } from '@shared/hooks';
import { dataGridCellHoverClass } from '@shared/styles';
import clsx from 'clsx';
import { CheckIcon } from '@shared/custom-icons';
import {
  columnsManagementSlotProps,
  DATA_GRID_ID,
  getInitialHiddenDataGridColumnsFromLocalStorage,
  getLLDate,
  saveHiddenDataGridColumnsToLocalStorage,
} from '@retail/utils';
import { getTogglableDataGridColumns } from '@shared/utils';
import { GridCallbackDetails } from '@mui/x-data-grid/models/api';
import { AssortmentTagChips } from '../AssortmentTag';
import { useRowColorGrouping } from '@retail/hooks';
import { ExternalItemSource, useMgItemAndExternalNumberColumn } from '../../MgSupplierItem';
import { getDiscontinuationStatus } from '@retail/products/utils';
import { ReadMore } from '@mui/icons-material';
import { useAppTFunction } from '@retail/app/i18n';

interface Props {
  items: MgSupplierItemSearchResult[];
  totalItems: number;
  pagination: Pagination;
  isLoading: boolean;
  isBrandLevel: boolean;
  onNavigateToMgItem?: (mgItemNumber: string) => void;
  externalItemSource: ExternalItemSource;
  selection?: {
    selectedItems: MgSupplierItemSearchResult[];
    selectItems: Dispatch<MgSupplierItemSearchResult[]>;
  };
  noActiveFilters: boolean;
  showProductOwner: boolean;
}

type GridColItem = GridColDef<MgSupplierItemSearchResult>;

const baseColumnProps: Partial<GridColItem> = {
  width: 170,
  headerName: '',
  headerAlign: 'left',
  align: 'left',
  sortable: false,
};

export function MgSupplierItemsTableWithCheckbox({
  items,
  totalItems,
  selection,
  pagination,
  isLoading,
  isBrandLevel,
  onNavigateToMgItem,
  externalItemSource,
  noActiveFilters,
  showProductOwner,
}: Props) {
  const t = useAppTFunction();
  const { getRowColorByIdGroups } = useRowColorGrouping();

  const selectionModel: GridRowSelectionModel = useMemo(
    () => ({
      type: 'include',
      ids: new Set(selection?.selectedItems.map((item) => item.mgSupplierItemId) || []),
    }),

    [selection?.selectedItems]
  );

  const onRowsSelectionHandler = useCallback(
    (selections: GridRowSelectionModel, details: GridCallbackDetails) => {
      if (details.reason === 'singleRowSelection') {
        const rowSelections: MgSupplierItemSearchResult[] = Array.from(selections.ids)
          .map((selection) => items?.find((item) => item.mgSupplierItemId === selection))
          .filter((selection): selection is MgSupplierItemSearchResult => selection !== undefined);

        selection?.selectItems(rowSelections);
        // When checking the "all items checkbox", we get 0 selections (bug?). Handle multiple (all/none) selections manually instead.
      } else if (selection?.selectedItems.length !== items.length) {
        selection?.selectItems(items);
      } else {
        selection.selectItems([]);
      }
    },
    [items, selection]
  );

  const mgItemAndExternalNumberColumn =
    useMgItemAndExternalNumberColumn<MgSupplierItemSearchResult>({
      columnProps: baseColumnProps,
      externalItemSource,
    });

  const columnConfigs: { show: boolean; column: GridColDef<MgSupplierItemSearchResult> }[] =
    useMemo(
      () => [
        {
          show: true,
          column: {
            ...baseColumnProps,
            field: 'inAssortment',
            headerName: '',
            headerAlign: 'center',
            align: 'center',
            width: 30,
            minWidth: 30,
            renderCell: ({ row }) =>
              row.inAssortment && (
                <Tooltip title={t('products.search.itemsInMyAssortment')}>
                  <Stack>
                    <CheckIcon />
                  </Stack>
                </Tooltip>
              ),
          },
        },
        {
          show: true,
          column: {
            ...baseColumnProps,
            field: 'primaryText',
            width: 330,
            headerName: t('products.mgItem.supplierItem.primaryText'),
            renderCell: ({ row }) => (
              <StackedTextCell
                topLine={row.primaryText}
                bottomLine={row.secondaryText}
                slotProps={{
                  topLine: { variant: 'body2', fontWeight: '600', maxLines: 2 },
                  bottomLine: { maxLines: 1 },
                }}
              />
            ),
          },
        },
        {
          show: true,
          column: mgItemAndExternalNumberColumn,
        },
        {
          show: true,
          column: {
            ...baseColumnProps,
            field: 'supplier',
            width: 250,
            headerName: t('products.supplier.name'),
            renderCell: ({ row }) => (
              <StackedTextCell topLine={row.participantAccountName} bottomLine={row.mgSupplierId} />
            ),
          },
        },
        {
          show: showProductOwner,
          column: {
            ...baseColumnProps,
            field: 'mainSupplierName',
            width: 180,
            headerName: t('products.mgItem.supplierItem.productOwner'),
            renderCell: ({ row }) => row.mainSupplierName ?? '-',
          },
        },
        {
          show: true,
          column: {
            ...baseColumnProps,
            field: 'supplierItemId',
            width: 150,
            headerName: t('products.mgItem.details.supplierItemId'),
          },
        },
        {
          show: true,
          column: {
            ...baseColumnProps,
            field: 'productGroup',
            width: 220,
            headerName: t('products.mgItem.supplierItem.bk04ProductCategory'),
            renderCell: ({ row }) => (
              <StackedTextCell
                topLine={row.bk04Varegruppe?.name}
                bottomLine={row.bk04Varegruppe?.groupNumber}
              />
            ),
          },
        },
        {
          show: true,
          column: {
            ...baseColumnProps,
            field: 'discountGroup',
            width: 220,
            headerName: t('products.search.discountGroup.label'),
            renderCell: ({ row }) => (
              <StackedTextCell
                topLine={row.discountGroup?.name}
                bottomLine={row.discountGroup?.code}
              />
            ),
          },
        },
        {
          show: isBrandLevel,
          column: {
            ...baseColumnProps,
            width: 220,
            field: 'assortmentTags',
            headerName: t('products.mgItem.supplierItem.subAssortment'),
            renderCell: ({ row }) => <AssortmentTagChips assortmentTags={row.tags} />,
          },
        },
        {
          show: true,
          column: {
            ...baseColumnProps,
            width: 160,
            field: 'discontinuation',
            headerName: t('products.mgItem.details.discontinuation'),
            renderCell: ({ row }) => (
              <StackedTextCell
                topLine={getDiscontinuationStatus(row.discontinuation?.discontinuationStatus)}
                bottomLine={getLLDate(row.discontinuation?.discontinuationDate)}
              />
            ),
          },
        },
        {
          show: !!onNavigateToMgItem,
          column: {
            ...baseColumnProps,
            field: 'actions',
            headerAlign: 'center',
            align: 'center',
            width: 44,
            minWidth: 44,
            renderCell: ({ row }) => (
              <Tooltip title={t('myAssortment.actions.seeProductInfo')}>
                <IconButton
                  onClick={
                    onNavigateToMgItem
                      ? () => onNavigateToMgItem(row.mgItemNumber.toString())
                      : () => null
                  }
                >
                  <ReadMore />
                </IconButton>
              </Tooltip>
            ),
          },
        },
      ],
      [isBrandLevel, mgItemAndExternalNumberColumn, onNavigateToMgItem, showProductOwner, t]
    );

  const columns = columnConfigs.filter(({ show }) => show).map(({ column }) => column);

  return (
    <PaginatedDataGrid<MgSupplierItemSearchResult>
      rows={items}
      columns={columns}
      loading={isLoading}
      getRowId={(row) => row.mgSupplierItemId}
      gridPagination={pagination}
      rowCount={totalItems}
      getRowClassName={(value) =>
        getRowColorByIdGroups(
          items.map((item) => item.mgItemNumber),
          value.row.mgItemNumber,
          value.indexRelativeToCurrentPage,
          true
        )
      }
      rowHeight={65}
      checkboxSelection
      disableRowSelectionOnClick
      disableColumnMenu
      onRowSelectionModelChange={onRowsSelectionHandler}
      rowSelectionModel={selectionModel}
      getCellClassName={({ field }) =>
        clsx({
          [dataGridCellHoverClass]: !['__check__'].includes(field),
        })
      }
      initialState={{
        columns: {
          columnVisibilityModel: {
            ...getInitialHiddenDataGridColumnsFromLocalStorage(
              DATA_GRID_ID.MG_SUPPLIER_ITEMS_TABLE,
              getTogglableDataGridColumns(columns)
            ),
          },
        },
        pinnedColumns: { right: ['actions'] },
      }}
      onColumnVisibilityModelChange={(_, details) => {
        const toggableColumns = getTogglableDataGridColumns(columns);
        saveHiddenDataGridColumnsToLocalStorage(
          DATA_GRID_ID.MG_SUPPLIER_ITEMS_TABLE,
          toggableColumns,
          details.api.state.columns.columnVisibilityModel
        );
      }}
      slots={{
        columnMenu: CustomColumnMenu,
        noRowsOverlay: () => (
          <Stack alignItems="center" justifyContent="center" gap={2} height="100%">
            {noActiveFilters || isLoading ? (
              <Typography
                variant="h6"
                fontWeight="medium"
                color="text.secondary"
                alignSelf="center"
              >
                {t('products.mgItem.search.infoText')}
              </Typography>
            ) : (
              <Typography variant="h3" color="text.secondary">
                {t('products.mgItem.noItems')}
              </Typography>
            )}
          </Stack>
        ),
      }}
      slotProps={{
        ...columnsManagementSlotProps,
      }}
    />
  );
}
